/**
 * The IANA time zone the browser reports for the user.
 * Browsers report their own canonical id, which can be an older alias of the current IANA id
 * (Chrome reports "Asia/Calcutta", while the IANA database lists "Asia/Kolkata").
 *
 * @returns {string|undefined}
 * @throws {Error} if Intl is not available
 */
export function detectTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The id the browser itself uses for a time zone, so aliases of one zone resolve to the same id.
 *
 * @param {string} timezone IANA time zone id
 * @returns {string|null} null if the browser does not know the id
 */
function canonicalTimezone(timezone) {
    try {
        return new Intl.DateTimeFormat(undefined, {timeZone: timezone}).resolvedOptions().timeZone;
    } catch {
        return null;
    }
}

/**
 * Finds which of the given time zone ids is the zone the browser detected: the same id, or the one that the
 * browser treats as the same zone (an alias). It never compares UTC offsets.
 *
 * @param {string} detected id from {@link detectTimezone}
 * @param {string[]} candidates IANA time zone ids
 * @returns {string|null} null if nothing matches, or if several candidates match (it would be a guess)
 */
export function findMatchingTimezone(detected, candidates) {
    if (candidates.includes(detected)) {
        return detected;
    }

    const aliases = candidates.filter((id) => canonicalTimezone(id) === detected);

    return aliases.length === 1 ? aliases[0] : null;
}
